import React from 'react';
import { Box, Button, Chip, Switch, Typography } from '@mui/material';
import { useLanguage } from '../../i18n/LanguageContext';
import { NodeCardShell } from './NodeCardShell';
import { NodeCardProps } from './NodeCardProps';

export const FanNodeCard: React.FC<NodeCardProps> = ({
  node, index, gateway, onToggle, onCommand, onEditOpen,
}) => {
  const { t } = useLanguage();
  const s = node.settings ?? {};
  const fanSpeed   = s.fanSpeed   ?? 'auto';
  const oscillation = s.oscillation ?? false;

  const handleOn  = () => { if (node.state !== true)  onToggle(); };
  const handleOff = () => { if (node.state !== false) onToggle(); };

  // Optimistic settings go through onCommand's own atomic update, not a separate
  // onSettingsChange call -- see NodeCardProps.onCommand. This matters most for
  // toggleOscillation, which computes `next` FROM the current value: a discarded
  // optimistic update there means `oscillation` never advances past its initial
  // false, so every click keeps computing next=true and only the "on" command
  // ever gets sent.
  const changeFan = (f: string) => {
    onCommand('setFanSpeed', { value: f }, { fanSpeed: f });
  };

  const toggleOscillation = () => {
    const next = !oscillation;
    onCommand('setOscillation', { value: next ? '1' : '0' }, { oscillation: next });
  };

  // xiaomi.fan.p85 (the only hardware-validated Mijia fan model) only supports 4
  // discrete levels -- no 'auto' mode, no 5th level (see miio-adapter.py's
  // _FAN_SPEED_LEVELS / _build_miio_command). SwitchBot's IR fan control, by
  // contrast, doesn't differentiate speed values at all (every level sends the
  // same learned "customize" button, see switchbot-adapter.py) so those extra
  // chips are harmless placeholders there and stay as-is.
  const speeds = gateway.kind === 'mijia'
    ? [
      { value: '1', label: '1' },
      { value: '2', label: '2' },
      { value: '3', label: '3' },
      { value: '4', label: '4' },
    ]
    : [
      { value: 'auto', label: t.acFanAuto },
      { value: '1', label: '1' },
      { value: '2', label: '2' },
      { value: '3', label: '3' },
      { value: '4', label: '4' },
      { value: '5', label: '5' },
    ];

  return (
    <NodeCardShell
      node={node} index={index} onEditOpen={onEditOpen}
      statelessOverride={gateway.kind === 'mijia' ? false : undefined}
    >
      <Box display="flex" gap={1} mb={1.5}>
        <Button
          variant={node.state !== true ? 'contained' : 'outlined'}
          color={node.state === false ? 'error' : 'inherit'}
          size="small" onClick={handleOff} disabled={node.isLoading} sx={{ flex: 1 }}
        >
          {t.statusOff}
        </Button>
        <Button
          variant={node.state === true ? 'contained' : 'outlined'}
          color={node.state === true ? 'success' : 'inherit'}
          size="small" onClick={handleOn} disabled={node.isLoading} sx={{ flex: 1 }}
        >
          {t.statusOn}
        </Button>
      </Box>

      <Typography variant="caption" color="text.secondary">{t.fanSpeed}</Typography>
      <Box display="flex" gap={0.5} mt={0.5} mb={1.5} flexWrap="wrap">
        {speeds.map(sp => (
          <Chip
            key={sp.value} label={sp.label} size="small"
            variant={fanSpeed === sp.value ? 'filled' : 'outlined'}
            onClick={() => changeFan(sp.value)}
          />
        ))}
      </Box>

      <Box display="flex" alignItems="center">
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
          {t.fanOscillation}
        </Typography>
        <Switch size="small" checked={oscillation} onChange={toggleOscillation} disabled={node.isLoading} />
      </Box>
    </NodeCardShell>
  );
};
